import React, { useMemo, useState } from 'react';
import { BaseTool, IToolContext, IToolManifest, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Bold, Italic as ItalicIcon, Underline as UnderlineIcon, Strikethrough as StrikethroughIcon, AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyCenter, RotateCcw, RotateCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { useTableCellStore } from '../../stores/tableCellStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { UpdateTableCellStylesCommand } from '../../core/commands/UpdateTableCellStylesCommand';
import { ClearTableCellStylesCommand } from '../../core/commands/ClearTableCellStylesCommand';
import { UpdateTableCellLayoutsCommand } from '../../core/commands/UpdateTableCellLayoutsCommand';
import { UpdateTableGridCommand } from '../../core/commands/UpdateTableGridCommand';
import type { TableCellStyle } from '../../domains/diagram/models/table/tableCell.types';
import type { TableCellKey } from '../../stores/tableCellStore';
import { cn } from '../../lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

// removed unused StyleField alias

const clampNum = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const presetFontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72];
const presetFontFamilies = [
  { id: 'inter', label: 'Inter', value: 'Inter, system-ui, -apple-system, sans-serif' },
  { id: 'system', label: 'System', value: 'system-ui, -apple-system, sans-serif' },
  { id: 'arial', label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { id: 'georgia', label: 'Georgia', value: 'Georgia, serif' },
  { id: 'times', label: 'Times', value: '"Times New Roman", Times, serif' },
  { id: 'mono', label: 'Monospace', value: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
];

// Keep controls compact by grouping them under a single "Text" popover (Type icon).

type ToolParams =
  | { action: 'apply'; patch: Partial<TableCellStyle> }
  | { action: 'clear' }
  | { action: 'layout'; algorithmId: 'fixed' | 'grid' }
  | { action: 'cellResize'; mode: 'auto' | 'manual' };

type HorizontalAlign = 'left' | 'center' | 'right';
type VerticalAlign = 'top' | 'middle' | 'bottom';
type TableCellLayoutSnapshot = { algorithmId?: unknown };

const readShapeAttrs = (shape: unknown): Record<string, unknown> => {
  if (!shape || typeof shape !== 'object') return {};
  const data = (shape as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return {};
  const attrs = (data as { attrs?: unknown }).attrs;
  return attrs && typeof attrs === 'object' && !Array.isArray(attrs) ? (attrs as Record<string, unknown>) : {};
};

const TableCellStyleToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const selectedIds = useMemo(() => Array.from(context.selection), [context.selection]);
  const isDisabled = selectedIds.length === 0;
  // selection state accessed directly when executing; no local selection deps needed here
  const selections = useTableCellStore((s) => s.selections);
  const firstSel = selectedIds.length > 0 ? selections[selectedIds[0]] : undefined;
  const selectedKeys = (firstSel?.selectedKeys ?? []) as TableCellKey[];
  const shape0 = selectedIds.length > 0 ? context.shapes.get(selectedIds[0]) : null;
  const attrs0 = useMemo(() => readShapeAttrs(shape0), [shape0]);
  const autoResizeCells = attrs0.autoResizeCells === true;
  const anchorKey = firstSel?.anchorKey ?? selectedKeys[0];
  const currentLayout = useMemo(() => {
    const map = attrs0.cellLayouts as Record<string, TableCellLayoutSnapshot> | undefined;
    if (!anchorKey) {
      if (!map || Object.keys(map).length === 0) return 'fixed' as const;
      return 'grid' as const;
    }
    const cfg = map?.[anchorKey];
    return cfg?.algorithmId === 'grid' ? ('grid' as const) : ('fixed' as const);
  }, [anchorKey, attrs0]);

  const [textColor, setTextColor] = useState('#0f172a');
  const [fontSize, setFontSize] = useState(14);
  const [fontFamily, setFontFamily] = useState(presetFontFamilies[0].value);
  const [fontWeight, setFontWeight] = useState<'normal' | 'bold'>('normal');
  const [textAlign, setTextAlign] = useState<HorizontalAlign>('center');
  const [verticalAlign, setVerticalAlign] = useState<VerticalAlign>('middle');
  const [fontStyle, setFontStyle] = useState<'normal' | 'italic'>('normal');
  const [textRotation, setTextRotation] = useState<0 | 90 | -90 | 180>(0);
  const [underline, setUnderline] = useState(false);
  const [strike, setStrike] = useState(false);
  const [textOptionsOpen, setTextOptionsOpen] = useState(false);

  const applyPatch = (patch: Partial<TableCellStyle>) => {
    onExecute?.({ action: 'apply', patch } satisfies ToolParams);
  };

  const setColor = (field: 'textColor', value: string) => {
    if (field === 'textColor') setTextColor(value);
    applyPatch({ [field]: value } as Partial<TableCellStyle>);
  };

  const applyFontSize = (value: number) => {
    const next = clampNum(value, 6, 72);
    setFontSize(next);
    applyPatch({ fontSize: next });
  };

  const applyFontFamily = (value: string) => {
    setFontFamily(value);
    applyPatch({ fontFamily: value });
  };

  const toggleTextStyle = (style: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    if (style === 'bold') {
      const next = fontWeight === 'bold' ? 'normal' : 'bold';
      setFontWeight(next);
      applyPatch({ fontWeight: next });
      return;
    }
    if (style === 'italic') {
      const next = fontStyle === 'italic' ? 'normal' : 'italic';
      setFontStyle(next);
      applyPatch({ fontStyle: next });
      return;
    }
    if (style === 'underline') {
      const next = !underline;
      setUnderline(next);
      const deco = next && strike ? 'underline line-through' : next ? 'underline' : strike ? 'line-through' : 'none';
      applyPatch({ textDecoration: deco });
      return;
    }
    const next = !strike;
    setStrike(next);
    const deco = underline && next ? 'underline line-through' : underline ? 'underline' : next ? 'line-through' : 'none';
    applyPatch({ textDecoration: deco });
  };

  const applyAlignment = (horizontal: HorizontalAlign, vertical: VerticalAlign) => {
    setTextAlign(horizontal);
    setVerticalAlign(vertical);
    applyPatch({ textAlign: horizontal, verticalAlign: vertical });
  };

  const applyRotation = (value: 0 | 90 | -90 | 180) => {
    setTextRotation(value);
    applyPatch({ textRotation: value });
  };

  const applyLayout = (algorithmId: 'fixed' | 'grid') => {
    onExecute?.({ action: 'layout', algorithmId } satisfies ToolParams);
  };

  const applyCellResize = (mode: 'auto' | 'manual') => {
    onExecute?.({ action: 'cellResize', mode } satisfies ToolParams);
  };

  const resolveAlignmentIcon = () => {
    if (textAlign === 'left') return AlignLeft;
    if (textAlign === 'right') return AlignRight;
    return AlignCenter;
  };
  const AlignmentIcon = resolveAlignmentIcon();

  const disabled = isDisabled;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-stretch gap-1 px-1">
        {/* Cell resize dropdown */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={isDisabled}
                    className={cn(
                      "h-8 w-8 rounded-md bg-background hover:bg-muted transition-colors disabled:opacity-50",
                      "flex items-center justify-center"
                    )}
                    aria-label={t('table.cellResize')}
                    data-testid="tid.tools.tableCellStyle.resize.trigger"
                  >
                    {autoResizeCells ? (
                      <img src="/assets/app-icons/table/cell-resize-auto.svg" alt={t('table.cellResizeAutoAlt')} className="h-4 w-4 dark:invert" />
                    ) : (
                      <img src="/assets/app-icons/table/cell-resize-manual.svg" alt={t('table.cellResizeManualAlt')} className="h-4 w-4 dark:invert" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    disabled={isDisabled}
                    data-testid="tid.tools.tableCellStyle.resize.auto"
                    onClick={() => applyCellResize('auto')}
                  >
                    {t('table.cellResizeAuto')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isDisabled}
                    data-testid="tid.tools.tableCellStyle.resize.manual"
                    onClick={() => applyCellResize('manual')}
                  >
                    {t('table.cellResizeManual')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.cellResize')}</TooltipContent>
        </Tooltip>

        {/* Cell layout dropdown */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={isDisabled}
                    className={cn(
                      "h-8 w-8 rounded-md bg-background hover:bg-muted transition-colors disabled:opacity-50",
                      "flex items-center justify-center"
                    )}
                    aria-label={t('table.cellLayout')}
                    data-testid="tid.tools.tableCellStyle.layout.trigger"
                  >
                    {currentLayout === 'fixed' ? (
                      <img src="/assets/app-icons/table/cell-layout-fixed.svg" alt={t('table.cellLayoutFixedAlt')} className="h-4 w-4 dark:invert" />
                    ) : (
                      <img src="/assets/app-icons/table/cell-layout-grid.svg" alt={t('table.cellLayoutGridAlt')} className="h-4 w-4 dark:invert" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    disabled={isDisabled}
                    data-testid="tid.tools.tableCellStyle.layout.fixed"
                    onClick={() => applyLayout('fixed')}
                  >
                    {t('table.cellLayoutFixed')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isDisabled}
                    data-testid="tid.tools.tableCellStyle.layout.grid"
                    onClick={() => applyLayout('grid')}
                  >
                    {t('table.cellLayoutGrid')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.cellLayout')}</TooltipContent>
        </Tooltip>

        {/* Separator between Layout and Text */}
        <div className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] dark:border-[#3f3f46] self-stretch -my-1.5 mx-1" />

        {/* Text options popover - positioned last before MoreVertical in toolbar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Popover open={textOptionsOpen} onOpenChange={setTextOptionsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={disabled}
                    className="relative h-8 w-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('table.textOptionsAria')}
                    data-testid="tid.tools.tableCellStyle.text.trigger"
                  >
                    <img src="/assets/app-icons/table/text.svg" alt={t('table.textIconAlt')} className="h-4 w-4 dark:invert" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-3" align="start" data-testid="tid.tools.tableCellStyle.text.panel">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-medium text-muted-foreground">{t('table.cellTextColorLabel')}</div>
                        <input
                          type="color"
                          value={textColor}
                          onChange={(e) => setColor('textColor', e.target.value)}
                          className="h-8 w-10 rounded border border-border bg-background"
                          disabled={disabled}
                          aria-label={t('table.cellTextColorAria')}
                          data-testid="tid.tools.tableCellStyle.text.color"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-medium text-muted-foreground">{t('table.cellFontSizeLabel')}</div>
                        <select
                          className="h-8 rounded border border-border bg-background px-2 text-xs text-foreground disabled:opacity-50"
                          disabled={disabled}
                          value={fontSize}
                          onChange={(e) => applyFontSize(parseInt(e.target.value, 10))}
                          aria-label={t('table.cellFontSizeAria')}
                          data-testid="tid.tools.tableCellStyle.text.font-size"
                        >
                          {presetFontSizes.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-medium text-muted-foreground">{t('table.cellFontFamilyLabel')}</div>
                        <select
                          className="h-8 rounded border border-border bg-background px-2 text-xs text-foreground disabled:opacity-50"
                          disabled={disabled}
                          value={fontFamily}
                          onChange={(e) => applyFontFamily(e.target.value)}
                          aria-label={t('table.cellFontFamilyAria')}
                          data-testid="tid.tools.tableCellStyle.text.font-family"
                        >
                          {presetFontFamilies.map((f) => (
                            <option key={f.id} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex items-center gap-2">
                      <Button
                        variant={fontWeight === 'bold' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => toggleTextStyle('bold')}
                        disabled={disabled}
                        data-testid="tid.tools.tableCellStyle.text.bold"
                      >
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={fontStyle === 'italic' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => toggleTextStyle('italic')}
                        disabled={disabled}
                        data-testid="tid.tools.tableCellStyle.text.italic"
                      >
                        <ItalicIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={underline ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => toggleTextStyle('underline')}
                        disabled={disabled}
                        data-testid="tid.tools.tableCellStyle.text.underline"
                      >
                        <UnderlineIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={strike ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => toggleTextStyle('strikethrough')}
                        disabled={disabled}
                        data-testid="tid.tools.tableCellStyle.text.strikethrough"
                      >
                        <StrikethroughIcon className="h-4 w-4" />
                      </Button>
                      <Separator orientation="vertical" className="h-6" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => applyRotation(-90)}
                        disabled={disabled}
                        aria-label={t('table.rotateCcwAria')}
                        data-testid="tid.tools.tableCellStyle.text.rotate-ccw"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={textRotation === 0 ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => applyRotation(0)}
                        disabled={disabled}
                        aria-label={t('table.resetRotationAria')}
                        data-testid="tid.tools.tableCellStyle.text.rotate-reset"
                      >
                        <span className="text-[10px] font-semibold">0°</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => applyRotation(90)}
                        disabled={disabled}
                        aria-label={t('table.rotateCwAria')}
                        data-testid="tid.tools.tableCellStyle.text.rotate-cw"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                    </div>

                    <Separator />

                    <div className="flex items-center gap-2">
                      <div className="text-[10px] font-medium text-muted-foreground">{t('table.cellTextAlignLabel')}</div>
                      <select
                        className="h-8 rounded border border-border bg-background px-2 text-xs text-foreground disabled:opacity-50"
                        disabled={disabled}
                        value={textAlign}
                        onChange={(e) => applyAlignment(e.target.value as HorizontalAlign, verticalAlign)}
                        aria-label={t('table.horizontalAlignAria')}
                        data-testid="tid.tools.tableCellStyle.text.horizontal-align"
                      >
                        <option value="left">{t('table.textHAlignLeft')}</option>
                        <option value="center">{t('table.textHAlignCenter')}</option>
                        <option value="right">{t('table.textHAlignRight')}</option>
                      </select>
                      <select
                        className="h-8 rounded border border-border bg-background px-2 text-xs text-foreground disabled:opacity-50"
                        disabled={disabled}
                        value={verticalAlign}
                        onChange={(e) => applyAlignment(textAlign, e.target.value as VerticalAlign)}
                        aria-label={t('table.verticalAlignAria')}
                        data-testid="tid.tools.tableCellStyle.text.vertical-align"
                      >
                        <option value="top">{t('table.textVAlignTop')}</option>
                        <option value="middle">{t('table.textVAlignMiddle')}</option>
                        <option value="bottom">{t('table.textVAlignBottom')}</option>
                      </select>
                      <div className="ml-auto flex items-center gap-1 text-muted-foreground">
                        <AlignmentIcon className="h-4 w-4" />
                        <AlignVerticalJustifyCenter className="h-4 w-4 opacity-0" />
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.textOptionsTooltip')}</TooltipContent>
        </Tooltip>

      </div>
    </TooltipProvider>
  );
};

export class TableCellStyleTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'table-cell-style',
    name: 'Table cell style',
    icon: 'table',
    category: 'style',
    description: 'Apply styles to selected table cells',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false,
    },
    applicability: {
      contextTypes: ['genericShape'],
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    if (context.selection.size === 0) return false;
    const store = useDiagramStore.getState();
    const selectedNodes = Array.from(context.selection).map((id) => store.nodes.find((n) => n.id === id)).filter(Boolean);
    return selectedNodes.length > 0 && selectedNodes.every((n) => n?.type === 'table');
  }

  execute(params?: ToolParams): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) {
      return null;
    }

    const selectionState = useTableCellStore.getState().selections;
    const nodeIds = Array.from(this.context.selection);
    const cellKeysByNodeId: Record<string, TableCellKey[]> = {};
    const clampCount = (value: unknown, fallback: number, min = 1): number => {
      if (typeof value === 'number' && Number.isFinite(value)) return Math.max(min, Math.floor(value));
      return fallback;
    };
    nodeIds.forEach((id) => {
      const keys = selectionState[id]?.selectedKeys ?? [];
      if (keys.length > 0) {
        cellKeysByNodeId[id] = keys;
        return;
      }
      const shape = this.context?.shapes?.get(id);
      const attrs = readShapeAttrs(shape);
      const rowCount = clampCount(attrs.rowCount, 3);
      const colCount = clampCount(attrs.columnCount, 3);
      const showHeader = attrs.showHeader !== false;
      const showHeaderColumn = attrs.showHeaderColumn === true;
      const rows = showHeader ? rowCount + 1 : rowCount;
      const cols = showHeaderColumn ? colCount + 1 : colCount;
      const all: TableCellKey[] = [];
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          all.push(`${r},${c}` as TableCellKey);
        }
      }
      cellKeysByNodeId[id] = all;
    });

    if (params.action === 'layout') {
      return new UpdateTableCellLayoutsCommand({
        nodeIds,
        cellKeysByNodeId,
        layout: { algorithmId: params.algorithmId },
      });
    }
    if (params.action === 'cellResize') {
      return new UpdateTableGridCommand(nodeIds, { field: 'autoResizeCells', value: params.mode === 'auto' });
    }
    if (params.action === 'clear') {
      return new ClearTableCellStylesCommand({ nodeIds, cellKeysByNodeId });
    }
    return new UpdateTableCellStylesCommand({ nodeIds, cellKeysByNodeId, stylePatch: params.patch as TableCellStyle });
  }

  getFloatingPanelComponent() {
    return TableCellStyleToolComponent;
  }
}
